"use client";

import * as React from "react";
import { Loader2, ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { siteConfig } from "@/lib/site-config";
import { useHydrated } from "@/lib/use-hydrated";

export default function LoginPage() {
  const [status, setStatus] = React.useState<"idle" | "submitting" | "error">("idle");
  const [error, setError] = React.useState<string | null>(null);
  const hydrated = useHydrated();

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("submitting");
    setError(null);

    const form = new FormData(event.currentTarget);

    const res = await fetch("/api/session/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        slug: form.get("slug"),
        email: form.get("email"),
        password: form.get("password"),
      }),
    });

    const body = await res.json();

    if (!body.ok) {
      setError(body.message);
      setStatus("error");
      return;
    }

    // Full browser navigation, not router.push() -- /dashboard immediately
    // redirects to /dashboard/onboarding server-side for a new tenant, and
    // a real navigation follows that HTTP redirect chain natively; the
    // App Router's client-side RSC-fetch navigation was observed to be
    // considerably slower to settle through a redirect than a real one.
    window.location.href = "/dashboard";
  }

  return (
    <div className="mx-auto max-w-md px-4 py-16 sm:px-6">
      <Card>
        <CardHeader>
          <CardTitle>Log in to your clinic</CardTitle>
          <CardDescription>Enter your clinic&apos;s address and your account details.</CardDescription>
        </CardHeader>
        <CardContent>
          {/* method="post" so a submit before hydration can never put the
              password in the URL. See useHydrated. */}
          <form onSubmit={handleSubmit} method="post" className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="slug">Clinic address</Label>
              <div className="flex items-center rounded-md border border-input bg-card focus-within:ring-2 focus-within:ring-ring">
                <Input id="slug" name="slug" placeholder="apollo" required className="border-0 focus-visible:ring-0" />
                <span className="pr-3 text-sm text-muted-foreground">.{siteConfig.rootDomain}</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" name="email" type="email" required placeholder="you@yourclinic.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" name="password" type="password" required />
            </div>

            {status === "error" && error && <p className="text-sm text-danger">{error}</p>}

            <Button type="submit" className="w-full" disabled={!hydrated || status === "submitting"}>
              {status === "submitting" && <Loader2 className="size-4 animate-spin" />}
              Log in
              <ArrowRight className="size-4" />
            </Button>
          </form>
          <p className="mt-4 text-center text-xs text-muted-foreground">
            Don&apos;t have an account yet?{" "}
            <a href="/signup" className="text-primary underline underline-offset-2">
              Start your free trial
            </a>
            .
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
